import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Select,
  TextField,
  Spinner,
  Banner,
  Checkbox,
  Modal,
  Badge,
} from '@shopify/polaris';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const SERVER_URL = getServerURL();

const PROVIDER_OPTIONS = [
  { label: 'Vertex', value: 'vertex' },
  { label: 'ComfyUI', value: 'comfyui' },
  { label: 'FAL.AI', value: 'fal-ai' },
];

const MODEL_PROVIDER_OPTIONS = [
  { label: 'Any provider', value: '' },
  ...PROVIDER_OPTIONS,
];

const COMFY_NODE_OPTIONS = [
  { label: 'Default (Gemini)', value: '' },
  { label: 'Gemini node', value: 'gemini' },
  { label: 'Flux2Pro node', value: 'flux2pro' },
  { label: 'Flux2Max partner node', value: 'flux2max' },
];

const createModelId = () => `model_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const normalizeProviderValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'vertex' || normalized === 'gemini') return 'vertex';
  if (normalized === 'comfyui' || normalized === 'flux2') return 'comfyui';
  if (normalized === 'fal-ai' || normalized === 'fal.ai' || normalized === 'fal-flux-max' || normalized === 'fal-ai-flux2-dev' || normalized === 'fal-flux2-dev' || normalized === 'flux2-dev') return 'fal-ai';
  return '';
};

const getProviderDisplayLabel = (value) => {
  const normalized = normalizeProviderValue(value);
  if (normalized === 'vertex') return 'Vertex';
  if (normalized === 'comfyui') return 'ComfyUI';
  if (normalized === 'fal-ai') return 'FAL.AI';
  return 'Any provider';
};

const getComfyNodeDisplayLabel = (value) => {
  const normalized = normalizeComfyNode(value);
  if (normalized === 'flux2max') return 'Flux2Max partner';
  if (normalized === 'flux2pro') return 'Flux2Pro';
  if (normalized === 'gemini') return 'Gemini';
  return 'Default';
};

const normalizeComfyNode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'gemini' || normalized === 'flux2pro' || normalized === 'flux2max') return normalized;
  return '';
};

const normalizePricing = (pricing = {}) => {
  const cost = Number(pricing?.cost_per_scene ?? 0);
  return {
    currency: String(pricing?.currency || 'USD').trim().toUpperCase() || 'USD',
    cost_per_scene: Number.isFinite(cost) && cost >= 0 ? Math.round(cost * 10000) / 10000 : 0,
  };
};

const normalizeModel = (model) => {
  const label = String(model?.label || '').trim();
  const value = String(model?.value || '').trim();
  const rawCapabilities = model?.capabilities && typeof model.capabilities === 'object'
    ? model.capabilities
    : {};
  const rawRouting = model?.routing && typeof model.routing === 'object'
    ? model.routing
    : {};
  return {
    id: String(model?.id || createModelId()).trim() || createModelId(),
    label,
    value,
    provider: normalizeProviderValue(model?.provider || ''),
    enabled: model?.enabled !== false,
    capabilities: {
      generation: model?.generation !== undefined
        ? model.generation !== false
        : (rawCapabilities.generation !== false),
      regeneration: model?.regeneration !== undefined
        ? model.regeneration !== false
        : (rawCapabilities.regeneration !== false),
    },
    routing: {
      comfyNode: normalizeProviderValue(model?.provider || '') === 'comfyui'
        ? normalizeComfyNode(model?.comfyNode || rawRouting.comfyNode || '')
        : '',
    },
    pricing: normalizePricing(model?.pricing),
  };
};

const getProviderScopedModelKey = (provider, value) => {
  const normalizedProvider = normalizeProviderValue(provider || '') || '__any__';
  const normalizedValue = String(value || '').trim().toLowerCase();
  return `${normalizedProvider}::${normalizedValue}`;
};

export const isModelCompatibleWithProvider = (models, value, provider, capability) => {
  const normalizedValue = String(value || '').trim().toLowerCase();
  const normalizedProvider = normalizeProviderValue(provider);
  if (!normalizedValue) return true;

  return models.some((model) => (
    String(model.value || '').trim().toLowerCase() === normalizedValue
    && model.enabled
    && model?.capabilities?.[capability] !== false
    && (!model.provider || model.provider === normalizedProvider)
  ));
};

export const buildDefaultModelOptions = (models, capability, provider, currentValue) => {
  const items = models
    .filter((model) => (
      model.enabled
      && model?.capabilities?.[capability] !== false
      && (!model.provider || model.provider === provider)
    ))
    .map((model) => ({ label: model.label || model.value, value: model.value }));
  const unique = [];
  const seen = new Set();

  items.forEach((item) => {
    const key = String(item.value || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(item);
  });

  if (currentValue && !seen.has(currentValue.toLowerCase())) {
    unique.unshift({ label: `${currentValue} (currently unavailable)`, value: currentValue });
  }

  return [{ label: 'Provider default', value: '' }, ...unique];
};

const RegenerationModelsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [generationSettingsId, setGenerationSettingsId] = useState('');
  const [defaultRegenerationProvider, setDefaultRegenerationProvider] = useState('vertex');
  const [defaultRegenerationModel, setDefaultRegenerationModel] = useState('');
  const [defaultGenerationProvider, setDefaultGenerationProvider] = useState('comfyui');
  const [defaultGenerationModel, setDefaultGenerationModel] = useState('');
  const [models, setModels] = useState([]);

  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [editingModelId, setEditingModelId] = useState('');
  const [modelForm, setModelForm] = useState({
    label: '',
    value: '',
    provider: '',
    enabled: true,
    generation: true,
    regeneration: true,
    comfyNode: '',
    pricingCurrency: 'USD',
    costPerScene: '0',
  });

  const enabledRegenerationModelOptions = useMemo(() => {
    return buildDefaultModelOptions(
      models,
      'regeneration',
      defaultRegenerationProvider,
      defaultRegenerationModel
    );
  }, [models, defaultRegenerationProvider, defaultRegenerationModel]);

  const enabledGenerationModelOptions = useMemo(() => {
    return buildDefaultModelOptions(
      models,
      'generation',
      defaultGenerationProvider,
      defaultGenerationModel
    );
  }, [models, defaultGenerationProvider, defaultGenerationModel]);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${SERVER_URL}/settings/generation/regeneration-models`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      const data = result?.data || {};
      const normalizedModels = Array.isArray(data.REGEN_MODEL_CATALOG)
        ? data.REGEN_MODEL_CATALOG.map(normalizeModel).filter((item) => item.value)
        : [];

      setGenerationSettingsId(String(data.generation_settings_id || '').trim());
      setDefaultRegenerationProvider(normalizeProviderValue(data.REGEN_DEFAULT_DYNAMIC_PROVIDER || 'vertex') || 'vertex');
      setDefaultRegenerationModel(String(data.REGEN_DEFAULT_MODEL || '').trim());
      setDefaultGenerationProvider(normalizeProviderValue(data.GENERATION_DEFAULT_PROVIDER || 'comfyui') || 'comfyui');
      setDefaultGenerationModel(String(data.GENERATION_DEFAULT_MODEL || '').trim());
      setModels(normalizedModels);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load regeneration model settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const openAddModelModal = () => {
    setEditingModelId('');
    setModelForm({ label: '', value: '', provider: '', enabled: true, generation: true, regeneration: true, comfyNode: '', pricingCurrency: 'USD', costPerScene: '0' });
    setIsModelModalOpen(true);
  };

  const openEditModelModal = (model) => {
    setEditingModelId(model.id);
    setModelForm({
      label: model.label,
      value: model.value,
      provider: model.provider || '',
      enabled: model.enabled !== false,
      generation: model?.capabilities?.generation !== false,
      regeneration: model?.capabilities?.regeneration !== false,
      comfyNode: normalizeComfyNode(model?.routing?.comfyNode || ''),
      pricingCurrency: model.pricing?.currency || 'USD',
      costPerScene: String(model.pricing?.cost_per_scene ?? 0),
    });
    setIsModelModalOpen(true);
  };

  const handleSaveModel = () => {
    const parsedCost = Number(modelForm.costPerScene);
    if (!Number.isFinite(parsedCost) || parsedCost < 0) {
      setErrorMessage('Cost per scene must be zero or greater');
      return;
    }
    const normalized = normalizeModel({
      id: editingModelId || createModelId(),
      ...modelForm,
      pricing: {
        currency: modelForm.pricingCurrency,
        cost_per_scene: parsedCost,
      },
    });
    if (!normalized.label || !normalized.value) {
      setErrorMessage('Model label and model value are required');
      return;
    }

    const duplicate = models.some((model) => {
      if (editingModelId && model.id === editingModelId) return false;
      return getProviderScopedModelKey(model.provider, model.value) === getProviderScopedModelKey(normalized.provider, normalized.value);
    });

    if (duplicate) {
      setErrorMessage('Model value already exists for this provider');
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setModels((prev) => {
      if (editingModelId) {
        return prev.map((item) => (item.id === editingModelId ? normalized : item));
      }
      return [...prev, normalized];
    });

    if (defaultRegenerationModel && defaultRegenerationModel === normalized.value) {
      if (!normalized.enabled || normalized?.capabilities?.regeneration === false) {
        setDefaultRegenerationModel('');
      }
    }
    if (defaultGenerationModel && defaultGenerationModel === normalized.value) {
      if (!normalized.enabled || normalized?.capabilities?.generation === false) {
        setDefaultGenerationModel('');
      }
    }

    setIsModelModalOpen(false);
  };

  const handleToggleEnabled = (targetModel) => {
    setModels((prev) => prev.map((item) => {
      if (item.id !== targetModel.id) return item;
      return { ...item, enabled: !item.enabled };
    }));

    if (targetModel.enabled && defaultRegenerationModel === targetModel.value) {
      setDefaultRegenerationModel('');
    }
    if (targetModel.enabled && defaultGenerationModel === targetModel.value) {
      setDefaultGenerationModel('');
    }
  };

  const handleSaveSettings = async () => {
    if (!generationSettingsId) {
      setErrorMessage('Generation settings document not found');
      return;
    }

    const normalizedModels = models.map(normalizeModel);
    const enabledRegenerationValues = new Set(
      normalizedModels
        .filter((model) => model.enabled && model?.capabilities?.regeneration !== false)
        .map((model) => model.value)
    );
    const enabledGenerationValues = new Set(
      normalizedModels
        .filter((model) => model.enabled && model?.capabilities?.generation !== false)
        .map((model) => model.value)
    );
    if (defaultRegenerationModel && !enabledRegenerationValues.has(defaultRegenerationModel)) {
      setErrorMessage('Default regeneration model must be enabled with regeneration capability');
      return;
    }
    if (defaultGenerationModel && !enabledGenerationValues.has(defaultGenerationModel)) {
      setErrorMessage('Default generation model must be enabled with generation capability');
      return;
    }

    if (!isModelCompatibleWithProvider(normalizedModels, defaultRegenerationModel, defaultRegenerationProvider, 'regeneration')) {
      setErrorMessage('Default regeneration provider/model are incompatible');
      return;
    }
    if (!isModelCompatibleWithProvider(normalizedModels, defaultGenerationModel, defaultGenerationProvider, 'generation')) {
      setErrorMessage('Default generation provider/model are incompatible');
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        GENERATION_DEFAULT_PROVIDER: defaultGenerationProvider,
        GENERATION_DEFAULT_MODEL: defaultGenerationModel,
        REGEN_DEFAULT_DYNAMIC_PROVIDER: defaultRegenerationProvider,
        REGEN_DEFAULT_MODEL: defaultRegenerationModel,
        REGEN_MODEL_CATALOG: normalizedModels,
      };

      const response = await fetch(`${SERVER_URL}/settings/generation/${generationSettingsId}/regeneration-models`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorPayload = null;
        try {
          errorPayload = await response.json();
        } catch (_) {
          errorPayload = null;
        }
        const errorText = errorPayload?.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorText);
      }

      setSuccessMessage('Model catalog settings saved successfully');
      await loadSettings();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save model catalog settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <Layout.Section>
        <Card>
          <div style={{ padding: 24 }}>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingLg" as="h1">Model Catalog</Text>
                <InlineStack gap="200">
                  <Button onClick={openAddModelModal}>Add Model</Button>
                  <Button variant="primary" onClick={handleSaveSettings} loading={isSaving}>
                    Save Changes
                  </Button>
                </InlineStack>
              </InlineStack>

              <Text variant="bodyMd" tone="subdued">
                One shared catalog powers generation and regeneration. Disabled models are blocked server-side.
              </Text>

              {errorMessage ? (
                <Banner tone="critical" onDismiss={() => setErrorMessage('')}>
                  <p>{errorMessage}</p>
                </Banner>
              ) : null}

              {successMessage ? (
                <Banner tone="success" onDismiss={() => setSuccessMessage('')}>
                  <p>{successMessage}</p>
                </Banner>
              ) : null}

              {isLoading ? (
                <InlineStack align="center" blockAlign="center" gap="200">
                  <Spinner size="small" />
                  <Text variant="bodyMd" tone="subdued">Loading model catalog settings...</Text>
                </InlineStack>
              ) : (
                <BlockStack gap="400">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                    <Select
                      label="Default Dynamic Regeneration Provider"
                      options={PROVIDER_OPTIONS}
                      value={defaultRegenerationProvider}
                      onChange={(provider) => {
                        setDefaultRegenerationProvider(provider);
                        if (!isModelCompatibleWithProvider(models, defaultRegenerationModel, provider, 'regeneration')) {
                          setDefaultRegenerationModel('');
                        }
                      }}
                    />
                    <Select
                      label="Default Regeneration Model"
                      options={enabledRegenerationModelOptions}
                      value={defaultRegenerationModel}
                      onChange={setDefaultRegenerationModel}
                    />
                    <Select
                      label="Default Generation Provider"
                      options={PROVIDER_OPTIONS}
                      value={defaultGenerationProvider}
                      onChange={(provider) => {
                        setDefaultGenerationProvider(provider);
                        if (!isModelCompatibleWithProvider(models, defaultGenerationModel, provider, 'generation')) {
                          setDefaultGenerationModel('');
                        }
                      }}
                    />
                    <Select
                      label="Default Generation Model"
                      options={enabledGenerationModelOptions}
                      value={defaultGenerationModel}
                      onChange={setDefaultGenerationModel}
                    />
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Label</th>
                          <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Model Value</th>
                          <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Provider</th>
                          <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Comfy Node</th>
                          <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Capabilities</th>
                          <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Cost</th>
                          <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Status</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid #e1e3e5' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {models.length === 0 ? (
                          <tr>
                            <td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#6d7175' }}>
                              No models configured. Add your first model.
                            </td>
                          </tr>
                        ) : models.map((model) => (
                          <tr key={model.id}>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3' }}>{model.label}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3', fontFamily: 'monospace' }}>{model.value}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3' }}>{getProviderDisplayLabel(model.provider)}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3' }}>{model.provider === 'comfyui' ? getComfyNodeDisplayLabel(model?.routing?.comfyNode || '') : '-'}</td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3' }}>
                              <InlineStack gap="200" wrap={false}>
                                <Badge tone={model?.capabilities?.generation !== false ? 'success' : 'attention'}>
                                  {model?.capabilities?.generation !== false ? 'Generation' : 'No Generation'}
                                </Badge>
                                <Badge tone={model?.capabilities?.regeneration !== false ? 'success' : 'attention'}>
                                  {model?.capabilities?.regeneration !== false ? 'Regeneration' : 'No Regeneration'}
                                </Badge>
                              </InlineStack>
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3' }}>
                              <Badge>{`${model.pricing?.currency || 'USD'} ${Number(model.pricing?.cost_per_scene || 0).toFixed(4)} / scene`}</Badge>
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3' }}>
                              <Badge tone={model.enabled ? 'success' : 'critical'}>
                                {model.enabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                            </td>
                            <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f2f3', textAlign: 'right' }}>
                              <InlineStack align="end" gap="200">
                                <Button size="slim" onClick={() => openEditModelModal(model)}>Edit</Button>
                                <Button
                                  size="slim"
                                  tone={model.enabled ? 'critical' : undefined}
                                  onClick={() => handleToggleEnabled(model)}
                                >
                                  {model.enabled ? 'Disable' : 'Enable'}
                                </Button>
                              </InlineStack>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </BlockStack>
              )}
            </BlockStack>
          </div>
        </Card>
      </Layout.Section>

      <Modal
        open={isModelModalOpen}
        onClose={() => setIsModelModalOpen(false)}
        title={editingModelId ? 'Edit model' : 'Add model'}
        primaryAction={{
          content: editingModelId ? 'Save model' : 'Add model',
          onAction: handleSaveModel,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setIsModelModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <TextField
              label="Model label"
              value={modelForm.label}
              onChange={(value) => setModelForm((prev) => ({ ...prev, label: value }))}
              autoComplete="off"
            />
            <TextField
              label="Model value"
              value={modelForm.value}
              onChange={(value) => setModelForm((prev) => ({ ...prev, value }))}
              autoComplete="off"
              placeholder="e.g. gemini-2.5-flash-image-preview"
            />
            <Select
              label="Preferred provider"
              options={MODEL_PROVIDER_OPTIONS}
              value={modelForm.provider}
              onChange={(value) => setModelForm((prev) => ({ ...prev, provider: value }))}
            />
            <Select
              label="Comfy dynamic node"
              options={COMFY_NODE_OPTIONS}
              value={modelForm.comfyNode}
              onChange={(value) => setModelForm((prev) => ({ ...prev, comfyNode: value }))}
              disabled={modelForm.provider !== 'comfyui'}
              helpText={modelForm.provider === 'comfyui'
                ? 'Controls dynamic story generation/regeneration routing for ComfyUI models.'
                : 'Available only when provider is ComfyUI.'}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
              <TextField
                label="Currency"
                value={modelForm.pricingCurrency}
                onChange={(value) => setModelForm((prev) => ({ ...prev, pricingCurrency: value }))}
                autoComplete="off"
              />
              <TextField
                label="Cost per scene"
                type="number"
                min={0}
                step={0.0001}
                value={modelForm.costPerScene}
                onChange={(value) => setModelForm((prev) => ({ ...prev, costPerScene: value }))}
                autoComplete="off"
                helpText="Estimated configured cost saved on each scene version."
              />
            </div>
            <Checkbox
              label="Enabled"
              checked={modelForm.enabled}
              onChange={(value) => setModelForm((prev) => ({ ...prev, enabled: value }))}
            />
            <Checkbox
              label="Supports Generation"
              checked={modelForm.generation}
              onChange={(value) => setModelForm((prev) => ({ ...prev, generation: value }))}
            />
            <Checkbox
              label="Supports Regeneration"
              checked={modelForm.regeneration}
              onChange={(value) => setModelForm((prev) => ({ ...prev, regeneration: value }))}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Layout>
  );
};

export default RegenerationModelsPage;
